import React, { useState, useEffect, useRef } from "react"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import SideBar from "../../../common/layout/sidebar.component"
import IntlProps from "../../../state/helpers/IntlProps"
import { Button, Form } from "react-bootstrap"
import { Link } from "react-router-dom"
import ConfirmButton from "../../../common/button/confirmbutton.component"
import { PriceLevelFormProps } from "../../../common/types"
import { PriceLevelFormAttributes } from "../../../state/price-level/types"
import ProductPriceListing from "../../product-price/components/ProductPriceListing"
import { ProductPriceWithProductInfo } from "../../../common/types/productPrice"
import Icon from "../../../common/icon/icon.component"
import ProductSearchModal from "../../components/ProductSearchModal"
import { PRODUCT_TYPE } from "../../../state/product/Types"
import ProductPriceService from "../../../state/product-price/ProductPriceService"
import LoadingSpinner from "../../../common/spinner/loading.component"
import { multiProductPriceCreatePayload } from "../../../utils"
import EventBus from "../../../common/EventBus"

const messages = defineMessages({
    "price_level.name_placeholder": {
        id: "product.price_level.price_level.name_placeholder",
        defaultMessage: "Price Level Name"
    },
    "price_level.form.edit.save_btn": {
        id: "product.price_level.price_level.form.edit.save_btn",
        defaultMessage: "Save"
    },
    "price_level.form.edit.name_blank_error": {
        id: "price_level.form.edit.name_blank_error",
        defaultMessage: "The name of the price level can not be blank"
    },
    "price_level.form.edit.name_long_error": {
        id: "price_level.form.edit.name_long_error",
        defaultMessage: "The name of the price level can not have more than 30 characters"
    },
    "price_level.form.edit.update_error": {
        id: "price_level.form.edit.update_error",
        defaultMessage: "Error updating the price level, please try again"
    },
    "price_level.form.edit.create_error": {
        id: "price_level.form.edit.create_error",
        defaultMessage: "Error creating price level, please try again"
    }
})

const PriceLevelUpdateForm = (props: PriceLevelFormProps & IntlProps) => {

    const [form, setForm] = useState<PriceLevelFormAttributes>({
        name: props.priceLevel?.getName() || ""
    })
    const [productPricesWithProductInfo, setProductPricesWithProductInfo] = useState<ProductPriceWithProductInfo[]>([])
    const [errors, setErrors] = useState({ name: "" })
    const [modal, setModal] = useState(<></>)
    const modalCallback = useRef<(() => void) | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        SideBar.activateMenu("product")
    }, [])

    useEffect(() => {
        setForm({
            name: props.priceLevel?.getName() || ""
        })
    }, [props.priceLevel?.getName()])

    useEffect(() => {
        if(typeof modalCallback.current === "function") {
            modalCallback.current()
        }
    }, [modal])

    const findFormErrors = () => {
        const { name } = form
        const newErrors = { name: "" }

        if (!name || name === "") {
            newErrors.name = props.intl.formatMessage(messages["price_level.form.edit.name_blank_error"])
        } else if (name.length > 30) {
            newErrors.name = props.intl.formatMessage(messages["price_level.form.edit.name_long_error"])
        }

        return newErrors
    }

    const setField = (field: string, value: string) => {
        setForm({
            ...form,
            [field]: value
        })

        if (errors[field]) {
            setErrors({
                ...errors,
                [field]: null
            })
        }
    }

    const handleListData = (items: ProductPriceWithProductInfo[]) => {
        setProductPricesWithProductInfo(items)
    }

    const upsertPriceLevel = (saveWithRedirect: boolean) => {
        props.onUpdateCallback(form, productPricesWithProductInfo, saveWithRedirect)
    }

    const handleSave = (saveWithRedirect: boolean) => {
        const newErrors = findFormErrors()

        if (newErrors.name.length > 0) {
            setErrors(newErrors)
        } else {
            upsertPriceLevel(saveWithRedirect)
        }
    }


    const onProductModalClose = async (selectedIds: Array<string>) => {
        setModal(<></>)

        if (selectedIds.length == 0 || !props.priceLevel?.getId()) {
            return
        }

        EventBus.dispatch(EventBus.TopicSendSuccessNotification, {
            message: (
                <FormattedMessage
                    id="product.forms.price_level_form.price_create_notification"
                    defaultMessage="The prices are being created. They will be available in the listing once the creation process is finished."
                />
            )
        })

        setLoading(true)
        const payload = multiProductPriceCreatePayload(selectedIds, props.priceLevel?.getId())
        try {
            await ProductPriceService.update(payload)
        } finally {
            setLoading(false)
        }
    }

    const onProductModalOpen = () => {
        setModal(
            <ProductSearchModal
                isModalChangedCallback={(_, selectedIds) => onProductModalClose(selectedIds)}
                isMultiSelect={true}
                productType={[PRODUCT_TYPE.SINGLE]}
                additionalFilters={{"{notOneOf}": {priceLevelIds: [props.priceLevel?.getId()]}}}
            />
        )
    }

    const isSaveButtonDisabled = !form.name
    let priceLevelListing = <></>
    if (props.priceLevel) {
        if (!loading) {
            priceLevelListing = (
                <ProductPriceListing
                    priceLevelId={props.priceLevel.getId()}
                    productPricesWithProductInfo={productPricesWithProductInfo}
                    onListingChangeCallback={handleListData}
                    onUpdateCallback={() => handleSave(false)}
                ></ProductPriceListing>
            )
        } else {
            priceLevelListing = <LoadingSpinner className={"mt-4"}/>
        }
    }
    return (
        <>
            {modal}
            <div className="row ui-has-modules">
                <div className="col-8">
                    <h2 className="m-0 custom-lead">
                        <FormattedMessage id="user.password.settings.component.price_level_edit"
                            defaultMessage="Price Level"/>
                    </h2>
                </div>
            </div>

            <Form>
                <div className="row ui-has-modules py-1 pt-4">
                    <div className="col-12 ui-module mb-0 d-flex justify-content-between">
                        <Form.Group className={"col-6"}>
                            <Form.Label htmlFor="name">
                                <FormattedMessage id="user.form.edit.new_price_level_label" defaultMessage="Name"/>
                            </Form.Label>

                            <Form.Control
                                value={form.name}
                                onChange={e => setField("name", e.target.value)}
                                placeholder={props.intl.formatMessage(messages["price_level.name_placeholder"])}
                                isInvalid={!!errors.name}
                            />
                            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                        </Form.Group>
                        {
                            props.priceLevel &&
                            <Form.Group className={"mt-auto"}>
                                <Button onClick={() => onProductModalOpen()}>
                                    <Icon value={"plus-circle"}/>
                                    <FormattedMessage id="user.form.edit.add_product_price_button"
                                        defaultMessage="Add a product to existing price level"/>
                                </Button>
                            </Form.Group>
                        }
                    </div>
                </div>

                <div className="mb-5">
                    {priceLevelListing}
                </div>

                <div className="row m-0 py-1 fixed-bottom pr-0 pr-md-5 pr-xl-0">
                    <div className={"col-lg-12 mx-auto col-xl-11 pt-3 pl-4 pr-4 pr-md-5 pr-xl-4"}>
                        <div className="col-12 ui-module sticky-bottom-module">
                            <div className="row">
                                <div className="col-5">
                                    {props.onDeleteCallback && (
                                        <ConfirmButton
                                            onModalRender={(m, c) => {
                                                setModal(m)
                                                modalCallback.current = c
                                            }}
                                            onClick={() => props.onDeleteCallback(props.priceLevel.getId())}
                                            className="btn btn-danger"
                                        >
                                            <FormattedMessage
                                                id="price_levels.form.edit.delete_btn"
                                                defaultMessage="Delete Price Level"
                                            />
                                        </ConfirmButton>
                                    )}
                                </div>

                                <div className="col-7 text-align-right">
                                    <Link to="/priceLevels/catalog" className="btn btn-light mr-2">
                                        <FormattedMessage id="user.form.edit.cancel_btn" defaultMessage="Cancel" />
                                    </Link>
                                    <Button
                                        variant="primary"
                                        onClick={() => handleSave(true)}
                                        disabled={isSaveButtonDisabled}
                                    >
                                        {props.intl.formatMessage(messages["price_level.form.edit.save_btn"])}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Form>
            {props.error && (
                <Form.Control.Feedback type="invalid">{props.intl.formatMessage(messages[props.error])}</Form.Control.Feedback>
            )}
        </>
    )
}

export default injectIntl(PriceLevelUpdateForm)



// WEBPACK FOOTER //
// ./src/product/price-level/forms/PriceLevelForm.tsx